import type { ContentImageFromUserFile } from '@/models/interfaces/ContentImage'
import {
  createEmptyResourceFilterOptions,
  type ResourceFilterOptions,
} from '@/models/interfaces/map/PlatformFilterOptions'
import type { ResourceMapFilters } from '@/models/interfaces/map/ResourceMapFilters'
import type { Resource, ResourceEvent } from '@/models/interfaces/Resource'
import { skipPlatformFilterOptionsEndpoint } from '@/config/env'
import { apiClient } from '@/plugins/axios/api'
import FileUploader from '@/services/files/FileUploader'
import { handleFileUpload } from '@/services/forms/FormService'
import { transformSymfonyRelationToIRIs } from '@/services/utils/UtilsService'
import type { BaseMediaObject } from '../../models/interfaces/object/MediaObject'

const RESOURCE_COLLECTION_MAX_ITEMS_PER_PAGE = 500

/** After a failed filter_options response in this tab, skip that URL for the rest of the session. */
let resourceFilterOptionsEndpointSkippedForSession = false

export class ResourceService {
  /**
   * GET /api/resources, optionally scoped to a commune perimeter (GET /api/resources?cmnCode=...):
   * a real spatial test (ST_Intersects) on geoData, not a "commune" field (Resource has none).
   * An unknown cmnCode 404s; like the atlas_categories/projects/infrastructures equivalent, that's
   * caught and treated as an empty result rather than surfaced as an error.
   */
  static async getAll(cmnCode?: string | null): Promise<Resource[]> {
    try {
      const response = await apiClient.get('/api/resources', {
        params: cmnCode ? { cmnCode } : undefined,
      })
      const member = response.data?.['hydra:member']
      return Array.isArray(member) ? member : []
    } catch {
      return []
    }
  }

  static async getFilterOptions(): Promise<ResourceFilterOptions> {
    const tryFilterOptionsEndpoint =
      !skipPlatformFilterOptionsEndpoint && !resourceFilterOptionsEndpointSkippedForSession
    if (tryFilterOptionsEndpoint) {
      try {
        const response = await apiClient.get<ResourceFilterOptions>(
          '/api/resources/filter_options',
          {
            validateStatus: (status) => status === 200 || status === 404 || status === 500,
          },
        )
        if (response.status === 200 && response.data) {
          const normalized = ResourceService.normalizeResourceFilterOptions(response.data)
          if (ResourceService.isResourceFilterOptionsPopulated(normalized)) {
            return normalized
          }
        }
        if (response.status === 500 || response.status === 404) {
          resourceFilterOptionsEndpointSkippedForSession = true
        }
      } catch {
        resourceFilterOptionsEndpointSkippedForSession = true
      }
    }
    try {
      const resources = await ResourceService.getAll()
      return ResourceService.deriveResourceFilterOptionsFromResources(resources)
    } catch {
      return createEmptyResourceFilterOptions()
    }
  }

  private static isResourceFilterOptionsPopulated(o: ResourceFilterOptions): boolean {
    return (
      o.type.length > 0 || o.administrativeScopes.length > 0 || o.thematics.length > 0
    )
  }

  /**
   * Builds facet lists from the resource collection when GET /api/resources/filter_options is unavailable.
   */
  static deriveResourceFilterOptionsFromResources(resources: Resource[]): ResourceFilterOptions {
    const types = new Set<string>()
    const scopes = new Set<string>()
    const thematics = new Set<string>()

    for (const r of resources) {
      const typeStr = String(r.type ?? '').trim()
      if (typeStr !== '') types.add(typeStr)
      for (const s of r.administrativeScopes ?? []) {
        const scopeStr = String(s).trim()
        if (scopeStr !== '') scopes.add(scopeStr)
      }
      for (const th of r.thematics ?? []) {
        const thStr = String(th).trim()
        if (thStr !== '') thematics.add(thStr)
      }
    }

    return {
      type: [...types].sort((a, b) => a.localeCompare(b)),
      administrativeScopes: [...scopes].sort((a, b) => a.localeCompare(b)),
      thematics: [...thematics].sort((a, b) => a.localeCompare(b)),
    }
  }

  static normalizeResourceFilterOptions(
    raw: ResourceFilterOptions | null | undefined,
  ): ResourceFilterOptions {
    const empty = createEmptyResourceFilterOptions()
    if (raw == null || typeof raw !== 'object') return empty
    return {
      type: Array.isArray(raw.type) ? raw.type.map((s) => String(s)) : empty.type,
      administrativeScopes: Array.isArray(raw.administrativeScopes)
        ? raw.administrativeScopes.map((s) => String(s))
        : empty.administrativeScopes,
      thematics: Array.isArray(raw.thematics) ? raw.thematics.map((s) => String(s)) : empty.thematics,
    }
  }

  /**
   * GET /api/resources with optional API Platform filters (query params match Resource properties).
   * Also applies client-side filtering when the API ignores unknown query params.
   * @param options.cmnCode Same commune-perimeter scoping as getAll(); combined with the other filters.
   */
  static async getCollection(
    filters: ResourceMapFilters,
    options?: { itemsPerPage?: number; cmnCode?: string | null },
  ): Promise<Resource[]> {
    const itemsPerPage = options?.itemsPerPage ?? RESOURCE_COLLECTION_MAX_ITEMS_PER_PAGE
    const cmnCode = options?.cmnCode
    const query: Record<string, string> = { itemsPerPage: String(itemsPerPage) }
    if (cmnCode) {
      query.cmnCode = cmnCode
    }
    const entries: [keyof ResourceMapFilters, string][] = [
      ['type', filters.type],
      ['administrativeScopes', filters.administrativeScopes],
      ['thematics', filters.thematics],
    ]
    for (const [key, value] of entries) {
      const trimmed = value.trim()
      if (trimmed !== '') {
        query[key] = trimmed
      }
    }
    let list: Resource[] = []
    try {
      const member = await apiClient
        .get('/api/resources', { params: query })
        .then((response) => response.data?.['hydra:member'])
      list = Array.isArray(member) ? member : []
    } catch {
      // Keep the commune scope on fallback so a filter-param quirk never leaks unscoped/wrong-commune data.
      list = await ResourceService.getAll(cmnCode)
    }
    return ResourceService.applyClientSideResourceMapFilters(list, filters)
  }

  static applyClientSideResourceMapFilters(
    resources: Resource[],
    filters: ResourceMapFilters,
  ): Resource[] {
    const type = filters.type.trim()
    const administrativeScopes = filters.administrativeScopes.trim()
    const thematics = filters.thematics.trim()

    return resources.filter((resource) => {
      if (type !== '' && String(resource.type ?? '').trim() !== type) return false
      if (administrativeScopes !== '') {
        const scopes = resource.administrativeScopes ?? []
        if (
          !scopes.some(
            (scope) => String(scope).toLowerCase() === administrativeScopes.toLowerCase(),
          )
        )
          return false
      }
      if (thematics !== '') {
        const th = resource.thematics ?? []
        if (
          !th.some((t) => String(t).toLowerCase() === thematics.toLowerCase())
        )
          return false
      }
      return true
    })
  }

  static async get(search: Partial<Resource>): Promise<Resource> {
    const { id } = search
    return await apiClient.get('/api/resources/' + id).then((response) => response.data)
  }

  static async post(resource: Resource): Promise<Resource> {
    resource = await handleFileUpload(resource)
    const createdResource = await apiClient
      .post('/api/resources', transformSymfonyRelationToIRIs(resource))
      .then((response) => response.data)
    return await this.uploadImagePreview(createdResource, resource.previewImageToUpload)
  }

  static async patch(resource: Resource): Promise<Resource> {
    resource = await handleFileUpload(resource)
    const updatedResource = await apiClient
      .patch('/api/resources/' + resource.id, transformSymfonyRelationToIRIs(resource))
      .then((response) => response.data)

    return await this.uploadImagePreview(updatedResource, resource.previewImageToUpload)
  }

  static async uploadImagePreview(
    resource: Resource,
    previewImageToUpload?: ContentImageFromUserFile,
  ): Promise<Resource> {
    let previewImage = resource?.previewImage
    if (previewImageToUpload) {
      previewImage = (await FileUploader.uploadMedia(previewImageToUpload.file)) as BaseMediaObject
      return await apiClient
        .patch('/api/resources/' + resource.id, transformSymfonyRelationToIRIs({ previewImage }))
        .then((response) => response.data)
    } else {
      return resource
    }
  }

  static async delete(resource: Resource): Promise<Resource> {
    return await apiClient.delete('/api/resources/' + resource.id).then((response) => response.data)
  }

  static async getNearestEvents(): Promise<ResourceEvent[]> {
    const res = await apiClient
      .get('/api/resources/events/nearest')
      .then((response) => response.data['hydra:member'])
    console.log(res)
    return res
  }

  static getLink(resource: Resource): string {
    return resource.file?.contentUrl || resource.link || ''
  }
}
